import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string().min(2).max(50).required(),
  provider: Yup.string().required(),

  phoneNumberId: Yup.string().when("provider", {
    is: "official",
    then: Yup.string().required("Requerido"),
  }),

  businessAccountId: Yup.string().when("provider", {
    is: "official",
    then: Yup.string().required("Requerido"),
  }),

  accessToken: Yup.string().when("provider", {
    is: "official",
    then: Yup.string().required("Requerido"),
  }),

  apiVersion: Yup.string().when("provider", {
    is: "official",
    then: Yup.string()
      .matches(/^v\d+\.\d+$/, "Formato inválido (ej: v18.0)")
      .required("Requerido"),
  }),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    greetingMessage: "",
    farewellMessage: "",
    isDefault: false,
    provider: "wwebjs",

    phoneNumberId: "",
    businessAccountId: "",
    accessToken: "",
    webhookVerifyToken: "",
    apiVersion: "v18.0",
  };

  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}`);

        // 🔥 FIX NORMALIZACIÓN
        setWhatsApp({
          ...initialState,
          ...data,
          phoneNumberId: data.phoneNumberId || "",
          businessAccountId: data.businessAccountId || "",
          accessToken: data.accessToken || "",
          webhookVerifyToken: data.verifyToken || "",
          apiVersion: data.apiVersion || "v18.0",
        });

        const whatsQueueIds = data.queues?.map(queue => queue.id);
        setSelectedQueueIds(whatsQueueIds);
      } catch (err) {
        toastError(err);
      }
    };

    fetchSession();
  }, [whatsAppId]);

  const handleSaveWhatsApp = async values => {
    const whatsappData = { ...values, queueIds: selectedQueueIds };

    console.log("ENVIANDO:", whatsappData); // 🔥 DEBUG

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
      } else {
        await api.post("/whatsapp", whatsappData);
      }

      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
  };

  return (
    <div className={classes.root}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>

        <Formik
          initialValues={whatsApp}
          enableReinitialize
          validationSchema={SessionSchema}
          onSubmit={(values, actions) => {
            handleSaveWhatsApp(values);
            actions.setSubmitting(false);
          }}
        >
          {({ values, setFieldValue, touched, errors, isSubmitting }) => {

            // 🔥 LIMPIAR CAMPOS AL CAMBIAR PROVIDER
            useEffect(() => {
              if (values.provider !== "official") {
                setFieldValue("phoneNumberId", "");
                setFieldValue("businessAccountId", "");
                setFieldValue("accessToken", "");
                setFieldValue("webhookVerifyToken", "");
                setFieldValue("apiVersion", "");
              }
            }, [values.provider]);

            return (
              <Form>
                <DialogContent dividers>

                  <div className={classes.multFieldLine}>
                    <Field
                      as={TextField}
                      label="Nombre"
                      name="name"
                      variant="outlined"
                      margin="dense"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />

                    <FormControlLabel
                      control={
                        <Field
                          as={Switch}
                          name="isDefault"
                          checked={values.isDefault}
                        />
                      }
                      label="Default"
                    />

                    <Field
                      as={TextField}
                      select
                      name="provider"
                      label="Proveedor"
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      SelectProps={{ native: true }}
                    >
                      <option value="baileys">Baileys (QR)</option>
                      <option value="wwebjs">WWebJS (QR)</option>
                      <option value="official">Cloud API</option>
                    </Field>
                  </div>

                  <Field
                    as={TextField}
                    label="Greeting"
                    name="greetingMessage"
                    fullWidth
                    multiline
                    rows={3}
                    margin="dense"
                    variant="outlined"
                  />

                  <Field
                    as={TextField}
                    label="Farewell"
                    name="farewellMessage"
                    fullWidth
                    multiline
                    rows={3}
                    margin="dense"
                    variant="outlined"
                  />

                  <QueueSelect
                    selectedQueueIds={selectedQueueIds}
                    onChange={setSelectedQueueIds}
                  />

                  {/* 🔥 CLOUD API */}
                  {values.provider === "official" && (
                    <>
                      <Field as={TextField} name="phoneNumberId" label="Phone Number ID" fullWidth margin="dense" />
                      <Field as={TextField} name="businessAccountId" label="Business Account ID" fullWidth margin="dense" />
                      <Field as={TextField} name="accessToken" label="Access Token" fullWidth margin="dense" />
                      <Field as={TextField} name="webhookVerifyToken" label="Verify Token" fullWidth margin="dense" />
                      <Field as={TextField} name="apiVersion" label="API Version" fullWidth margin="dense" />
                    </>
                  )}

                </DialogContent>

                <DialogActions>
                  <Button onClick={handleClose}>Cancelar</Button>

                  <Button type="submit" color="primary" variant="contained">
                    Guardar
                    {isSubmitting && (
                      <CircularProgress size={24} className={classes.buttonProgress} />
                    )}
                  </Button>
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);